import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getEducation, getNews, getEstimatedRates } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Scroll, Clock, ArrowRight, Newspaper, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info, ExternalLink, RefreshCw } from 'lucide-react';

const IMPACT_COLORS = {
  bullish: 'bg-green-500/10 text-green-400 border-green-500/30',
  bearish: 'bg-red-500/10 text-red-400 border-red-500/30',
  neutral: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
};

const IMPACT_EXPLANATIONS = {
  bullish: 'Bullish news suggests prices may rise. This indicates positive market sentiment for precious metals.',
  bearish: 'Bearish news suggests prices may fall. This indicates negative market sentiment or selling pressure.',
  neutral: 'Neutral news has no clear directional impact. Markets may react based on other factors.',
};

export default function EducationPage() {
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [estimates, setEstimates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [eduRes, newsRes, estRes] = await Promise.all([getEducation(), getNews(), getEstimatedRates()]);
        setArticles(eduRes.data);
        setNews(newsRes.data);
        setEstimates(estRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const categories = ['all', ...new Set(articles.map(a => a.category))];
  const filtered = filter === 'all' ? articles : articles.filter(a => a.category === filter);

  return (
    <div className="min-h-screen bg-background" data-testid="education-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <Scroll className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">Learn</h1>
          </div>
          <p className="text-muted-foreground mb-8">Knowledge, verified news, and market insights</p>
        </motion.div>

        <Tabs defaultValue="news" className="mb-8">
          <TabsList className="bg-secondary/50 border border-border/40">
            <TabsTrigger value="news" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-news">
              <Newspaper className="w-3.5 h-3.5 mr-1.5" /> News
            </TabsTrigger>
            <TabsTrigger value="articles" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-articles">
              <Scroll className="w-3.5 h-3.5 mr-1.5" /> Guides
            </TabsTrigger>
            <TabsTrigger value="estimates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-estimates">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Rate Forecast
            </TabsTrigger>
          </TabsList>

          {/* NEWS TAB */}
          <TabsContent value="news">
            {/* Legend/Explanation Bar */}
            <TooltipProvider>
              <div className="flex flex-wrap items-center gap-4 mt-6 mb-4 p-4 bg-card/50 border border-border/30 rounded-sm">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Info className="w-3.5 h-3.5" />
                  <span className="font-medium">Impact Legend:</span>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-xs text-green-400 font-medium">Bullish</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-card border-border">
                    <p className="text-xs">{IMPACT_EXPLANATIONS.bullish}</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-xs text-red-400 font-medium">Bearish</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-card border-border">
                    <p className="text-xs">{IMPACT_EXPLANATIONS.bearish}</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />
                      <span className="text-xs text-yellow-400 font-medium">Neutral</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-card border-border">
                    <p className="text-xs">{IMPACT_EXPLANATIONS.neutral}</p>
                  </TooltipContent>
                </Tooltip>
                <div className="h-4 w-px bg-border/50" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-xs text-blue-400 font-medium">Verified</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-card border-border">
                    <p className="text-xs">Verified news is from trusted, reputable sources and has been fact-checked for accuracy.</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <RefreshCw className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-xs text-purple-400 font-medium">Auto-Updated</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-card border-border">
                    <p className="text-xs">News is automatically fetched from multiple sources every 30 minutes to keep you informed.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>

            <div className="space-y-4">
              {news.length === 0 && !loading && <p className="text-muted-foreground text-center py-8">No news yet</p>}
              {news.map((n, i) => (
                <motion.div
                  key={n.news_id}
                  className="bg-card border border-border/40 hover:border-primary/20 rounded-sm p-5 transition-colors group"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`news-${n.news_id}`}
                >
                  <div className="flex items-start gap-4">
                    {n.image_url ? (
                      <img src={n.image_url} alt="" className="w-20 h-20 rounded-sm object-cover flex-shrink-0 bg-muted" />
                    ) : (
                      <div className={`w-14 h-14 rounded-sm flex items-center justify-center flex-shrink-0 ${
                        n.impact === 'bullish' ? 'bg-green-500/10' : n.impact === 'bearish' ? 'bg-red-500/10' : 'bg-yellow-500/10'
                      }`}>
                        {n.impact === 'bullish' ? <TrendingUp className="w-6 h-6 text-green-400" /> :
                         n.impact === 'bearish' ? <TrendingDown className="w-6 h-6 text-red-400" /> :
                         <AlertTriangle className="w-6 h-6 text-yellow-400" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className={`text-xs rounded-sm cursor-help ${IMPACT_COLORS[n.impact] || IMPACT_COLORS.neutral}`}>
                                {n.impact === 'bullish' && <TrendingUp className="w-3 h-3 mr-1" />}
                                {n.impact === 'bearish' && <TrendingDown className="w-3 h-3 mr-1" />}
                                {n.impact === 'neutral' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                {n.impact?.toUpperCase()}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-card border-border">
                              <p className="text-xs">{IMPACT_EXPLANATIONS[n.impact] || IMPACT_EXPLANATIONS.neutral}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {n.metals_affected?.map(m => (
                          <Badge key={m} variant="outline" className={`text-xs rounded-sm ${m === 'gold' ? 'border-primary/40 text-primary' : 'border-silver/40 text-silver'}`}>
                            {m.toUpperCase()}
                          </Badge>
                        ))}
                        {n.verified && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-xs rounded-sm border-blue-500/30 text-blue-400 cursor-help">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Verified
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs bg-card border-border">
                                <p className="text-xs">From a trusted, fact-checked source.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {n.auto_fetched && (
                          <Badge variant="outline" className="text-xs rounded-sm border-purple-500/30 text-purple-400">
                            <RefreshCw className="w-3 h-3 mr-1" /> Auto
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-display font-bold mb-1 group-hover:text-primary transition-colors">{n.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{n.summary}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-medium">{n.source}</span>
                          <span>{new Date(n.published_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {n.url && (
                          <a href={n.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                            Read more <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* ARTICLES TAB */}
          <TabsContent value="articles">
            <div className="flex items-center gap-2 my-6 overflow-x-auto pb-2" data-testid="education-filters">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors flex-shrink-0 ${
                    filter === cat ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:text-foreground border border-border/40'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filtered.map((article, i) => (
                <motion.div key={article.content_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                  <Link to={`/learn/${article.content_id}`} data-testid={`education-card-${article.content_id}`}>
                    <div className="bg-card border border-border/40 hover:border-primary/30 rounded-sm overflow-hidden group transition-all duration-300">
                      <div className="aspect-[2/1] overflow-hidden">
                        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-xs rounded-sm border-white/20 text-muted-foreground uppercase">{article.category}</Badge>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {article.read_time} min</span>
                        </div>
                        <h3 className="font-display text-xl font-bold mb-2 group-hover:text-primary transition-colors">{article.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{article.summary}</p>
                        <div className="flex items-center gap-1 text-primary text-sm font-medium mt-4">Read More <ArrowRight className="w-3 h-3" /></div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* ESTIMATED RATES TAB */}
          <TabsContent value="estimates">
            {estimates && (
              <div className="mt-6 space-y-6">
                {/* Current + trend */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card border border-primary/20 rounded-sm p-5">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Gold Current</p>
                    <p className="font-display text-3xl font-bold text-primary">Rs {estimates.gold_current.toLocaleString()}/g</p>
                    <p className={`text-sm mt-1 ${estimates.gold_trend_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {estimates.gold_trend_percent >= 0 ? '+' : ''}{estimates.gold_trend_percent}% trend (30d)
                    </p>
                  </div>
                  <div className="bg-card border border-silver/20 rounded-sm p-5">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Silver Current</p>
                    <p className="font-display text-3xl font-bold text-silver">Rs {estimates.silver_current.toLocaleString()}/g</p>
                    <p className={`text-sm mt-1 ${estimates.silver_trend_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {estimates.silver_trend_percent >= 0 ? '+' : ''}{estimates.silver_trend_percent}% trend (30d)
                    </p>
                  </div>
                </div>

                {/* Estimates table */}
                <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
                  <div className="p-4 border-b border-border/40">
                    <h3 className="font-display font-bold">Estimated Future Rates</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border/40">
                          <th className="text-left p-3 text-xs text-muted-foreground uppercase">Date</th>
                          <th className="text-right p-3 text-xs text-primary uppercase">Gold Est. /g</th>
                          <th className="text-right p-3 text-xs text-silver uppercase">Silver Est. /g</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estimates.estimates.map((e, i) => (
                          <tr key={e.date} className="border-b border-border/40 last:border-0 hover:bg-white/[0.02]">
                            <td className="p-3 font-mono text-xs">{e.date_label} <span className="text-muted-foreground">(+{e.days_ahead}d)</span></td>
                            <td className="p-3 text-right font-mono">Rs {e.gold_estimated.toLocaleString()}</td>
                            <td className="p-3 text-right font-mono">Rs {e.silver_estimated.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-sm p-4 flex items-start gap-3" data-testid="rate-disclaimer">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-200/70 leading-relaxed">{estimates.disclaimer}</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {loading && (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        )}
      </div>
    </div>
  );
}
